import { svgEditServiceInstance } from './svg-edit-service';

/**
 * Handler for SelectCopy notification.
 * Receives one element xmlId and selects it directly in the SVG canvas.
 */
function handleSelectCopy(xmlId: string | null) {
    if (!svgEditServiceInstance.canvas) {
        console.warn('[CopyEventHandlers] Cannot select copy elements before SVG canvas is ready.');
        return;
    }

    if (!xmlId) {
        console.warn('[CopyEventHandlers] SelectCopy event did not include an xmlId.');
        return;
    }

    const element = findElementByXmlId(xmlId);
    if (!(element instanceof SVGGraphicsElement)) {
        console.warn('[CopyEventHandlers] SelectCopy did not resolve any element.', { xmlId });
        return;
    }

    svgEditServiceInstance.canvas.selectOnly([element], true);
    svgEditServiceInstance.canvas.setMode('select');
}

/**
 * Normalizes SelectCopy event detail to a single xmlId.
 */
function getSelectCopyXmlId(detail: unknown): string | null {
    if (typeof detail === 'string') {
        return detail;
    }

    if (!detail || typeof detail !== 'object') {
        return null;
    }

    if ('xmlId' in detail && typeof detail.xmlId === 'string') {
        return detail.xmlId;
    }

    if ('XmlId' in detail && typeof detail.XmlId === 'string') {
        return detail.XmlId;
    }

    if ('XmlIds' in detail && Array.isArray(detail.XmlIds)) {
        const first = detail.XmlIds.find((id): id is string => typeof id === 'string');
        return first ?? null;
    }

    return null;
}

/**
 * Finds an SVG element by its xmlId or decoded xmlId name.
 */
function findElementByXmlId(xmlId: string): Element | null {
    const element = document.getElementById(xmlId);
    if (element) return element;
    
    // Try decoding the xmlId
    const decodedId = decodeXmlIdName(xmlId);
    return document.getElementById(decodedId);
}

/**
 * Decodes an XML ID that may contain hex-encoded characters.
 * Converts _xXXXX_ patterns to their character representation.
 */
function decodeXmlIdName(xmlId: string): string {
    return xmlId.replace(/_x([0-9a-fA-F]{4})_/g, (_, hex: string) => 
        String.fromCharCode(parseInt(hex, 16))
    );
}


/**
 * Registers event listeners for copy-related notifications.
 * Call this function during application initialization.
 */
export function registerCopyEventListeners(): void {
    // Register SelectCopy handler
    window.addEventListener('zaa-select-copy-received', (event) => {
        if (event instanceof CustomEvent) {
            const xmlId = getSelectCopyXmlId(event.detail);
            handleSelectCopy(xmlId);
        }
    });

}

/**
 * Export all handlers for direct invocation if needed.
 */
export const copyEventHandlers = {
    handleSelectCopy,
};